/**
 * 旅行カード（一覧用）
 *
 * カバー写真を背景に、タイトル・期間・訪問した都道府県・数字を重ねて表示する。
 */

import { Camera, MapPin, Sparkles } from 'lucide-react'

import type { Trip } from '@/api/types/trip'
import { Badge } from '@/components/ui/badge'
import { shortPrefectureName } from '@/lib/prefectures'
import { cn } from '@/lib/utils'
import { ImmichThumbnail } from '@/pages/activity/components/activity-card/immich-thumbnail'

import { dateRangeLabel, durationLabel } from './trip-utils'

type Props = {
  trip: Trip
  onClick: () => void
  className?: string
}

export const TripCard = ({ trip, onClick, className }: Props) => {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative block w-full overflow-hidden rounded-xl border bg-card p-0 text-left shadow-sm',
        className,
      )}
    >
      <div className="relative aspect-video w-full bg-muted">
        {trip.cover_asset_id ? (
          <ImmichThumbnail assetId={trip.cover_asset_id} alt={trip.title} size="preview" className="h-full w-full" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            <MapPin className="size-8" />
          </div>
        )}

        {/* 下から黒のグラデーションを重ねて文字を読ませる */}
        <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/20 to-transparent" />

        <div className="absolute inset-x-0 bottom-0 p-3 text-white">
          <div className="mb-1 flex items-center gap-1.5">
            <Badge variant="secondary" className="bg-white/20 text-white backdrop-blur-sm">
              {durationLabel(trip)}
            </Badge>
            {trip.status === 'draft' && (
              <Badge variant="secondary" className="bg-amber-500/80 text-white backdrop-blur-sm">
                未確認
              </Badge>
            )}
            {trip.has_story && <Sparkles className="size-4 text-amber-300" />}
          </div>
          <h3 className="truncate text-base font-semibold">{trip.title}</h3>
          <p className="truncate text-xs text-white/80">{dateRangeLabel(trip)}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 px-3 py-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <MapPin className="size-3.5" />
          {trip.place_count}ヶ所
        </span>
        <span className="flex items-center gap-1">
          <Camera className="size-3.5" />
          {trip.photo_count}枚
        </span>
        <span className="ml-auto truncate">{trip.prefectures.map(shortPrefectureName).join('・') || '地域不明'}</span>
      </div>
    </button>
  )
}
